import { createElement, lazy } from 'react';
import type { RouteObject } from 'react-router';

import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';

const RoleHomeRedirect = lazy(() =>
  import('./components/RoleHomeRedirect').then((module) => ({ default: module.RoleHomeRedirect })),
);
const ExecutiveHomePage = lazy(() =>
  import('./pages/ExecutiveHomePage').then((module) => ({ default: module.ExecutiveHomePage })),
);
const HrHomePage = lazy(() =>
  import('./pages/HrHomePage').then((module) => ({ default: module.HrHomePage })),
);
const EmployeeHomePage = lazy(() =>
  import('./pages/EmployeeHomePage').then((module) => ({ default: module.EmployeeHomePage })),
);
const TrainingCampaignListPage = lazy(() =>
  import('./pages/TrainingCampaignListPage').then((module) => ({
    default: module.TrainingCampaignListPage,
  })),
);
const TrainingCampaignDetailPage = lazy(() =>
  import('./pages/TrainingCampaignDetailPage').then((module) => ({
    default: module.TrainingCampaignDetailPage,
  })),
);
const ManualReaderPage = lazy(() =>
  import('./pages/ManualReaderPage').then((module) => ({ default: module.ManualReaderPage })),
);
const MyManualsPage = lazy(() =>
  import('./pages/MyManualsPage').then((module) => ({ default: module.MyManualsPage })),
);
const BenefitsPage = lazy(() =>
  import('./pages/BenefitsPage').then((module) => ({ default: module.BenefitsPage })),
);
const ManagerHomePage = lazy(() =>
  import('./pages/ManagerHomePage').then((module) => ({ default: module.ManagerHomePage })),
);

/**
 * Role landing pages. The index route redirects each user to their own home
 * based on `role_codes` from `/auth/me/`; each home is guarded by role so a
 * user does not land on a hub built for someone else.
 */
export const homeRoutes: RouteObject[] = [
  { index: true, element: createElement(RoleHomeRedirect) },
  {
    path: 'gerencia',
    element: createElement(ProtectedRoute, { view: 'dashboard' }),
    children: [{ index: true, element: createElement(ExecutiveHomePage) }],
  },
  {
    path: 'rrhh',
    element: createElement(ProtectedRoute, { view: 'hrHome' }),
    children: [{ index: true, element: createElement(HrHomePage) }],
  },
  {
    path: 'inicio',
    element: createElement(ProtectedRoute, { view: 'employeeHome' }),
    children: [
      { index: true, element: createElement(EmployeeHomePage) },
      {
        path: 'capacitaciones',
        children: [
          { index: true, element: createElement(TrainingCampaignListPage) },
          {
            path: ':id',
            element: createElement(TrainingCampaignDetailPage),
          },
          {
            path: ':id/manuales/:versionId',
            element: createElement(ProtectedRoute, {
              view: 'manualReader',
            }),
            children: [{ index: true, element: createElement(ManualReaderPage) }],
          },
        ],
      },
      { path: 'beneficios', element: createElement(BenefitsPage) },
    ],
  },
  // Top level, not under the EMPLOYEE-only `inicio` parent: the manual library
  // is for every role. Its view is ungated today; the backend permission that
  // opens it to all lands later.
  {
    path: 'manuales',
    element: createElement(ProtectedRoute, { view: 'myManuals' }),
    children: [{ index: true, element: createElement(MyManualsPage) }],
  },
  {
    path: 'jefe-area',
    element: createElement(ProtectedRoute, { view: 'managerHome' }),
    children: [{ index: true, element: createElement(ManagerHomePage) }],
  },
];
